import { 
  Mountain, 
  Building, 
  Waves, 
  Globe, 
  Factory, 
  Map as MapIcon, 
  Landmark, 
  Castle, 
  Home, 
  Radio, 
  Trees, 
  Church,
  LandPlot,
  Ship,
  Flower2,
  MountainSnow,
  ParkingSquare,
  Sunrise,
  Building2,
  BarChart,
  Wind,
  Warehouse,
  Pyramid,
  Antenna,
  Sprout,
  Cable,
  Tent,
  TowerControl,
  Router,
  Compass,
  CircleDashed
} from 'lucide-react';
import type { ReactNode } from 'react';

export type ViewType = 
  | 'Mountain(s)' 
  | 'Bridge' 
  | 'Chimney' 
  | 'Coast' 
  | 'Island' 
  | 'Skyline' 
  | 'Water Body'
  | string;

interface ViewTypeIconProps {
  type: ViewType | ViewType[];
  size?: number;
  className?: string;
}

// Helper function to match type with icon
const matchType = (typeValue: string, iconName: string): boolean => {
  return true;
};

export function ViewTypeIcon({ type, size = 16, className = '' }: ViewTypeIconProps) {
  // Extra check - if type is null/undefined/empty, fall back to Mountain
  if (!type) {
    return <Mountain size={size} className={className} />;
  }
  
  const getIconForType = (typeValue: string): ReactNode => {
    // Extra check for empty strings
    if (!typeValue || typeValue.trim() === '') {
      return <Mountain size={size} className={className} />;
    }
    
    // Normalize the type for comparison
    const normalizedType = typeValue.trim().toLowerCase();
    
    // Match against normalized values with unique icons for each type
    
    // Mountains and Terrain
    if (normalizedType.includes('mountain')) {
      if (normalizedType.includes('peak')) {
        matchType(normalizedType, 'MountainSnow (peak)');
        return <MountainSnow size={size} className={className} />;
      }
      if (normalizedType.includes('range')) {
        matchType(normalizedType, 'MountainSnow (range)');
        return <MountainSnow size={size} className={className} />;
      }
      matchType(normalizedType, 'Mountain');
      return <Mountain size={size} className={className} />;
    }
    if (normalizedType.includes('peak')) {
      matchType(normalizedType, 'MountainSnow (peak)');
      return <MountainSnow size={size} className={className} />;
    }
    if (normalizedType.includes('volcano')) {
      matchType(normalizedType, 'Pyramid (volcano)');
      return <Pyramid size={size} className={className} />;
    }
    if (normalizedType.includes('hill')) {
      matchType(normalizedType, 'Sunrise (hill)');
      return <Sunrise size={size} className={className} />;
    }
    if (normalizedType.includes('cliff')) {
      matchType(normalizedType, 'Mountain (cliff)');
      return <Mountain size={size} className={className} />;
    }
    
    // Structures and Infrastructure
    if (normalizedType.includes('bridge')) {
      matchType(normalizedType, 'Cable (bridge)');
      return <Cable size={size} className={className} />;
    }
    if (normalizedType.includes('chimney')) {
      matchType(normalizedType, 'Factory (chimney)');
      return <Factory size={size} className={className} />;
    }
    if (normalizedType.includes('tower')) {
      matchType(normalizedType, 'TowerControl (tower)');
      return <TowerControl size={size} className={className} />;
    }
    if (normalizedType.includes('lighthouse')) {
      matchType(normalizedType, 'Antenna (lighthouse)');
      return <Antenna size={size} className={className} />;
    }
    if (normalizedType.includes('windmill') || normalizedType.includes('turbine')) {
      matchType(normalizedType, 'Wind (windmill/turbine)');
      return <Wind size={size} className={className} />;
    }
    if (normalizedType.includes('power')) {
      matchType(normalizedType, 'Radio (power)');
      return <Radio size={size} className={className} />;
    }
    if (normalizedType.includes('factory')) {
      matchType(normalizedType, 'Factory');
      return <Factory size={size} className={className} />;
    }
    if (normalizedType.includes('industrial')) {
      matchType(normalizedType, 'Warehouse (industrial)');
      return <Warehouse size={size} className={className} />;
    }
    if (normalizedType.includes('building')) {
      matchType(normalizedType, 'Building');
      return <Building size={size} className={className} />;
    }
    
    // Water features
    if (normalizedType.includes('coast')) {
      matchType(normalizedType, 'Ship (coast)');
      return <Ship size={size} className={className} />;
    }
    if (normalizedType.includes('bay')) {
      matchType(normalizedType, 'Ship (bay)');
      return <Ship size={size} className={className} />;
    }
    if (normalizedType.includes('beach')) {
      matchType(normalizedType, 'Ship (beach)');
      return <Ship size={size} className={className} />;
    }
    if (normalizedType.includes('ocean')) {
      matchType(normalizedType, 'Globe (ocean)');
      return <Globe size={size} className={className} />;
    }
    if (normalizedType.includes('sea')) {
      matchType(normalizedType, 'Globe (sea)');
      return <Globe size={size} className={className} />;
    }
    if (normalizedType.includes('lake')) {
      matchType(normalizedType, 'Waves (lake)');
      return <Waves size={size} className={className} />;
    }
    if (normalizedType.includes('river')) {
      matchType(normalizedType, 'Waves (river)');
      return <Waves size={size} className={className} />;
    }
    if (normalizedType.includes('water')) {
      matchType(normalizedType, 'Waves (water)');
      return <Waves size={size} className={className} />;
    }
    
    // Urban environments
    if (normalizedType.includes('skyline')) {
      matchType(normalizedType, 'Building2 (skyline)');
      return <Building2 size={size} className={className} />;
    }
    if (normalizedType.includes('cityscape')) {
      matchType(normalizedType, 'Building2 (cityscape)');
      return <Building2 size={size} className={className} />;
    }
    if (normalizedType.includes('city')) {
      matchType(normalizedType, 'ParkingSquare (city)');
      return <ParkingSquare size={size} className={className} />;
    }
    if (normalizedType.includes('urban')) {
      matchType(normalizedType, 'Building2 (urban)');
      return <Building2 size={size} className={className} />;
    }
    if (normalizedType.includes('downtown')) {
      matchType(normalizedType, 'Building2 (downtown)');
      return <Building2 size={size} className={className} />;
    }
    if (normalizedType.includes('castle')) {
      matchType(normalizedType, 'Castle');
      return <Castle size={size} className={className} />;
    }
    if (normalizedType.includes('palace')) {
      matchType(normalizedType, 'Castle (palace)');
      return <Castle size={size} className={className} />;
    }
    if (normalizedType.includes('church')) {
      matchType(normalizedType, 'Church');
      return <Church size={size} className={className} />;
    }
    if (normalizedType.includes('cathedral')) {
      matchType(normalizedType, 'Church (cathedral)');
      return <Church size={size} className={className} />;
    }
    if (normalizedType.includes('temple')) {
      matchType(normalizedType, 'Landmark (temple)');
      return <Landmark size={size} className={className} />;
    }
    if (normalizedType.includes('monument')) {
      matchType(normalizedType, 'Landmark (monument)');
      return <Landmark size={size} className={className} />;
    }
    if (normalizedType.includes('statue')) {
      matchType(normalizedType, 'Landmark (statue)');
      return <Landmark size={size} className={className} />;
    }
    if (normalizedType.includes('house')) {
      matchType(normalizedType, 'Home');
      return <Home size={size} className={className} />;
    }
    
    // Geographic locations
    if (normalizedType.includes('island')) {
      matchType(normalizedType, 'MapIcon (island)');
      return <MapIcon size={size} className={className} />;
    }
    if (normalizedType.includes('peninsula')) {
      matchType(normalizedType, 'LandPlot (peninsula)');
      return <LandPlot size={size} className={className} />;
    }
    if (normalizedType.includes('isthmus')) {
      matchType(normalizedType, 'LandPlot (isthmus)');
      return <LandPlot size={size} className={className} />;
    }
    if (normalizedType.includes('canyon')) {
      matchType(normalizedType, 'Compass (canyon)');
      return <Compass size={size} className={className} />;
    }
    if (normalizedType.includes('valley')) {
      matchType(normalizedType, 'Sunrise (valley)');
      return <Sunrise size={size} className={className} />;
    }

    // Nature features
    if (normalizedType.includes('park')) {
      matchType(normalizedType, 'Trees (park)');
      return <Trees size={size} className={className} />;
    }
    if (normalizedType.includes('garden')) {
      matchType(normalizedType, 'Flower2 (garden)');
      return <Flower2 size={size} className={className} />;
    }
    if (normalizedType.includes('forest')) {
      matchType(normalizedType, 'Trees (forest)');
      return <Trees size={size} className={className} />;
    }
    if (normalizedType.includes('jungle')) {
      matchType(normalizedType, 'Sprout (jungle)');
      return <Sprout size={size} className={className} />;
    }
    if (normalizedType.includes('field')) {
      matchType(normalizedType, 'Flower2 (field)');
      return <Flower2 size={size} className={className} />;
    }
    if (normalizedType.includes('meadow')) {
      matchType(normalizedType, 'Flower2 (meadow)');
      return <Flower2 size={size} className={className} />;
    }
    
    // Catch-all for specific terms
    if (normalizedType.includes('camp')) {
      matchType(normalizedType, 'Tent (camp)');
      return <Tent size={size} className={className} />;
    }
    if (normalizedType.includes('antenna') || normalizedType.includes('communication')) {
      matchType(normalizedType, 'Router (antenna/communication)');
      return <Router size={size} className={className} />;
    }
    if (normalizedType.includes('road') || normalizedType.includes('highway')) {
      matchType(normalizedType, 'BarChart (road/highway)');
      return <BarChart size={size} className={className} />;
    }
    
    // Fallback to CircleDashed if no match, so we can easily see which types weren't matched
    return <CircleDashed size={size} className={className} />;
  };
  
  // Handle array of types (use first one for display)
  if (Array.isArray(type)) {
    if (type.length > 0) {
      return <>{getIconForType(type[0])}</>;
    }
    return <Mountain size={size} className={className} />;
  }
  
  // Handle string type
  if (typeof type === 'string') {
    return <>{getIconForType(type)}</>;
  }
  
  // Fallback for any other case
  return <Mountain size={size} className={className} />;
}

// Get type name for display
export function getViewTypeName(type: ViewType | ViewType[]): string {
  if (!type) return 'Unknown';
  
  if (Array.isArray(type) && type.length > 0) {
    return type[0];
  }
  if (typeof type === 'string') {
    return type;
  }
  return 'Unknown';
} 